import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
  type ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { CoverageHistoryTrendPoint } from '@/components/smartai/api/getCoverageHistory';
import {
  pctFromSummary,
  trendChartAxisTickLines,
  trendPointTooltipTitle,
} from './coverage-history-utils';
import { trendChartPanelClass } from './coverage-trend-layout';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface CoverageTrendChartProps {
  points: CoverageHistoryTrendPoint[];
  metric: 'lines' | 'inventory' | 'both';
}

export default function CoverageTrendChart({
  points,
  metric,
}: CoverageTrendChartProps) {
  const chartData = useMemo<ChartData<'line'>>(() => {
    const labels = points.map(p => trendChartAxisTickLines(p)[0] ?? '');
    const loc = points.map(p => pctFromSummary(p.summary, 'lines'));
    const inv = points.map(p => pctFromSummary(p.summary, 'inventory'));

    const datasets = [];
    if (metric === 'lines' || metric === 'both') {
      datasets.push({
        label: 'Lines of code %',
        data: loc,
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
        tension: 0.25,
        fill: metric === 'lines',
        spanGaps: true,
      });
    }
    if (metric === 'inventory' || metric === 'both') {
      datasets.push({
        label: 'Inventory %',
        data: inv,
        borderColor: '#16a34a',
        backgroundColor: 'rgba(22, 163, 74, 0.06)',
        tension: 0.25,
        fill: metric === 'inventory',
        spanGaps: true,
      });
    }

    return { labels, datasets };
  }, [points, metric]);

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { bottom: 8 } },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 10,
            font: { size: 11 },
            padding: 14,
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        tooltip: {
          callbacks: {
            title(items) {
              const idx = items[0]?.dataIndex;
              if (idx == null || idx < 0 || idx >= points.length) return '';
              return trendPointTooltipTitle(points[idx]);
            },
            label(ctx) {
              const v = ctx.parsed.y;
              if (v == null || Number.isNaN(v)) return `${ctx.dataset.label}: —`;
              return `${ctx.dataset.label}: ${v.toFixed(1)}%`;
            },
          },
        },
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          grid: { color: 'rgba(43, 33, 24, 0.06)' },
          ticks: { callback: v => `${v}%`, font: { size: 11 } },
          title: {
            display: true,
            text: 'Coverage %',
            font: { size: 11, weight: 'bold' },
          },
        },
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            padding: 8,
            font: { size: 10 },
            callback(_tickValue, index) {
              const point = points[index];
              if (!point) return '';
              const lines = trendChartAxisTickLines(point);
              return lines.length > 1 ? lines : lines[0] ?? '';
            },
          },
          title: {
            display: true,
            text: 'Build Number - Date',
            font: { size: 11, weight: 'bold' },
            padding: { top: 10 },
          },
        },
      },
    }),
    [points],
  );

  if (!points.length) {
    return (
      <div
        className={`${trendChartPanelClass} py-10 text-center text-[0.8125rem] text-[var(--inv-text-muted)]`}>
        No build coverage history yet. Run scans and calculate coverage for this
        branch to populate the trend.
      </div>
    );
  }

  return (
    <div className={trendChartPanelClass}>
      <div className="h-[min(24rem,52vh)] w-full min-w-0">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}
